import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

const ProgrammPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  useSEO({
    title: 'Unser Programm für Bad Windsheim - AfD Kommunalwahl 2026',
    description: 'Das kommunalpolitische Programm der AfD Bad Windsheim: Bürgernähe, bezahlbares Wohnen, Sicherheit, Gesundheit und Umweltschutz. Unser Plan für eine lebenswerte Stadt.',
    keywords: 'AfD Programm Bad Windsheim, Kommunalwahl Programm, Bürgernähe, bezahlbares Wohnen, Stadtentwicklung',
    canonical: `${siteUrl}/programm`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Programm AfD Bad Windsheim',
      description: 'Unser kommunalpolitisches Programm für Bad Windsheim',
      url: `${siteUrl}/programm`,
      mainEntity: {
        '@type': 'Article',
        headline: 'Kommunalpolitisches Programm AfD Bad Windsheim 2026',
        author: {
          '@type': 'Organization',
          name: 'AfD Bad Windsheim'
        },
        about: [
          'Bürgernähe und Offenheit',
          'Migration und Integration',
          'Bezahlbares und attraktives Wohnen',
          'Gesundheit',
          'Umweltschutz'
        ]
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-primary-600 to-primary-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-sm font-semibold tracking-wider uppercase">Unser Programm</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Für Bad Windsheim
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl">
            Unser kommunalpolitisches Programm für eine lebenswerte, sichere und zukunftsfähige Stadt.
          </p>
        </div>
      </section>

      {/* Logo Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary-200 to-secondary-200 rounded-2xl opacity-20"></div>
            <img 
              src="https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/ad92e76f525a3092b6db20d0bc38e3ae.png"
              alt="AfD Bad Windsheim Logo"
              className="relative w-full max-w-2xl mx-auto rounded-2xl shadow-2xl border-4 border-white"
            />
          </div>
        </div>
      </section>

      {/* Program Content */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          
          {/* Bürgernähe und Offenheit */}
          <div className="mb-20">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Grundsatz</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Bürgernähe und Offenheit</h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Bürgerbefragungen</h3>
                <p className="text-gray-700 leading-relaxed">
                  Ihre Meinung zählt! Wir möchten regelmäßig Ihre Anregungen und Ideen durch Befragungen einholen, um die Stadtentwicklung bürgernah zu gestalten.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Unterstützung der Belange aller Ortsteile</h3>
                <p className="text-gray-700 leading-relaxed">
                  Regelmäßige Ortsteil-Begehungen mit allen Vertretern der Fraktionen und der Stadtverwaltung sowie den betroffenen Bürgern.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Verbesserung der Infrastruktur</h3>
                <p className="text-gray-700 leading-relaxed">
                  Wir kümmern uns um die Pflege und den Ausbau unserer Straßen, Radwege und Barrierefreiheit.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Verbindliche Satzungen für Behinderte und Senioren</h3>
                <p className="text-gray-700 leading-relaxed">
                  Barrierefreiheit und Seniorenfreundlichkeit sind uns wichtig. Wir schaffen klare Rahmenbedingungen für ein inklusives Miteinander.
                </p>
              </div>
            </div>
          </div>

          {/* Migration und Integration */}
          <div className="mb-20">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Gesellschaft</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Migration und Integration</h2>
            
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Wir stehen für eine offene Gesellschaft, die jedoch klare Regeln und Erwartungen an alle stellt, die hier leben möchten.
            </p>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Klare Regeln und Rechtsstaatlichkeit</h3>
                <p className="text-gray-700 leading-relaxed">
                  Wir setzen uns konsequent dafür ein, dass alle bestehenden Gesetze angewendet werden, insbesondere im Umgang mit Kriminalität, Extremismus und missbräuchlicher Nutzung sozialer Leistungen. Wer Unfrieden stiftet oder unsere Werte nicht respektiert, hat hier keinen Platz.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Wertebasierte Integration</h3>
                <p className="text-gray-700 leading-relaxed">
                  Wir heißen alle Menschen willkommen, die ein Aufenthaltsrecht haben, arbeiten wollen oder schon berufstätig sind und sich in unsere Gesellschaft integrieren. Dabei ist uns die Akzeptanz unserer christlichen Werte und Traditionen besonders wichtig.
                </p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl border-l-4 border-primary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Transparenz bei Ausgaben</h3>
                <p className="text-gray-700 leading-relaxed">
                  Wir fordern volle Transparenz darüber, wie und wofür Gelder im Bereich Migration ausgegeben werden und stellen sicher, dass dies im Einklang mit den Prioritäten für alle Bürger steht. Gibt es noch andere ‚Ausgaben-Töpfe‘ aus denen zum Beispiel die Kosten für Betreuung und für die privaten Sicherheitsdienste finanziert werden, die nicht öffentlich gemacht werden?
                </p>
              </div>
            </div>
          </div>

          {/* Bezahlbares Wohnen */}
          <div className="mb-20">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Lebensqualität</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Bezahlbares und attraktives Wohnen</h2>
            
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Wir setzen auf eine nachhaltige und inklusive Entwicklung, die das Wohnen, die Sicherheit, die Infrastruktur und das gesellschaftliche Zusammenleben stärkt. Dabei stehen die Bedürfnisse unserer Bürger, insbesondere unserer Familien, im Mittelpunkt.
            </p>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-secondary-50 to-white p-6 rounded-xl border-l-4 border-secondary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Bezahlbares und attraktives Wohnen</h3>
                <p className="text-gray-700 leading-relaxed">
                  Unterstützung privater und städtischer Investitionen, Sanierung von Wohngebäuden, Familien mit Kindern bevorzugen bei Wohnungsvergabe, Leerstandsbeseitigung in der Altstadt und in den Ortsteilen, Nutzung aller Förderprogramme.
                </p>
              </div>

              <div className="bg-gradient-to-br from-secondary-50 to-white p-6 rounded-xl border-l-4 border-secondary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Sicherheit und Lebensqualität</h3>
                <p className="text-gray-700 leading-relaxed">
                  Energiesparende Straßenbeleuchtung, mehr Sicherheitspräsenz durch Kameras, Verbesserung der Beleuchtung in dunklen Bereichen.
                </p>
              </div>

              <div className="bg-gradient-to-br from-secondary-50 to-white p-6 rounded-xl border-l-4 border-secondary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Verkehrsanbindung und Digitalisierung</h3>
                <p className="text-gray-700 leading-relaxed">
                  Optimale Anbindung an den Großraum Nürnberg, Ausbau eines schnellen und stabilen Internets im gesamten Stadtgebiet und in den Ortsteilen.
                </p>
              </div>

              <div className="bg-gradient-to-br from-secondary-50 to-white p-6 rounded-xl border-l-4 border-secondary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Stadtentwicklung und Wirtschaft</h3>
                <p className="text-gray-700 leading-relaxed">
                  Gezielte Neuplanung von Gewerbegebieten, um Arbeitsplätze zu schaffen und wirtschaftliches Wachstum zu fördern, intelligentes Parkraum-Konzept.
                </p>
              </div>

              <div className="bg-gradient-to-br from-secondary-50 to-white p-6 rounded-xl border-l-4 border-secondary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Freizeit und Tourismus</h3>
                <p className="text-gray-700 leading-relaxed">
                  Unterstützung des Freilandmuseums und der Franken-Therme als touristische Anziehungspunkte, Förderung von Freizeitangeboten für Jugendliche, Familien und Senioren.
                </p>
              </div>

              <div className="bg-gradient-to-br from-secondary-50 to-white p-6 rounded-xl border-l-4 border-secondary-600">
                <h3 className="text-xl font-bold mb-3 text-gray-900">Inklusion und Gesellschaft</h3>
                <p className="text-gray-700 leading-relaxed">
                  Förderung des Inklusionscafés der Lebenshilfe und ehrenamtliches Engagement.
                </p>
              </div>
            </div>
          </div>

          {/* Gesundheit */}
          <div className="mb-20">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Versorgung</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Gesundheit</h2>
            
            <div className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-xl border-l-4 border-primary-600">
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Starkes Krankenhaus</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                Wir setzen uns für den Erhalt und Ausbau unseres Krankenhauses als unverzichtbarer Bestandteil der medizinischen Versorgung ein. Preisgünstige Praxisräume für die ambulante medizinische Versorgung.
              </p>
            </div>
          </div>

          {/* Umweltschutz */}
          <div className="mb-20">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-12 h-1 bg-secondary-600"></div>
              <span className="text-secondary-600 font-semibold uppercase tracking-wider text-sm">Nachhaltigkeit</span>
            </div>
            <h2 className="text-4xl font-bold mb-8 text-gray-900">Umweltschutz</h2>
            
            <div className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-xl border-l-4 border-primary-600">
              <p className="text-gray-700 leading-relaxed text-lg mb-4">
                Alle Umweltschutzmaßnahmen der Stadt und des Kreises müssen sinnvoll, bezahlbar und wohl durchdacht sein. Dies gilt auch für die Errichtung von neuen Solar-Anlagen auf Feldern, vor allem, weil sie dann die Pachtflächen für die übrigen Landwirte verteuern.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                Für alle Umweltschutzmaßnahmen müssen deshalb auch Alternativen überprüft werden.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Program Images */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Unser Wahlprogramm im Detail</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div 
              className="rounded-xl overflow-hidden shadow-xl border-4 border-white cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105"
              onClick={() => setZoomedImage('https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/50dde880d16e649f25c599a41a044064.jpeg')}
            >
              <img 
                src="https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/50dde880d16e649f25c599a41a044064.jpeg"
                alt="Wahlprogramm Innenseite"
                className="w-full h-auto"
              />
            </div>
            <div 
              className="rounded-xl overflow-hidden shadow-xl border-4 border-white cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105"
              onClick={() => setZoomedImage('https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/d7f4ba6d1e7580bee8dce64ee19542e9.jpeg')}
            >
              <img 
                src="https://static.readdy.ai/image/2461e108107748ba3465ce747c79b9cd/d7f4ba6d1e7580bee8dce64ee19542e9.jpeg"
                alt="Wahlprogramm Außenseite"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button
              className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-300 z-10"
              onClick={() => setZoomedImage(null)}
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
            <img 
              src={zoomedImage}
              alt="Vergrößertes Bild"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-secondary-600 to-secondary-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Gemeinsam für Bad Windsheim
          </h2>
          <p className="text-xl mb-8 text-red-100">
            Unterstützen Sie unsere Arbeit für eine lebenswerte Stadt mit Zukunft.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/kandidaten" 
              className="bg-white text-secondary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Unsere Kandidaten
            </Link>
            <Link 
              to="/" 
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Zur Startseite
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgrammPage;
